import { SettingsManager } from "@viamap/viamap2-common";
import { GroupInfo, SingleSetting } from "src/components/SettingsEditor";
import { JobSettingsDef as JobSettingsDefBoth } from "./JobSettingsDefBoth";
import { JobSettingsDef as JobSettingsDefNoTransit } from "./JobSettingsDefNoTransit";
import { InputFieldType } from "./JobState";

export type JobServiceCallerInfo = {
   userRef: string;
   userName: string;
   organizationId: string;
   languageCode: "da" | "en";
}

export type SingleTranslation = {
   da: string,
   en: string
}

export type InputFieldSpec = {
   title: SingleTranslation,
   valueKey?: string,
   type: InputFieldType,
   optionsKey?: string,
   disAllowDefault?: boolean
}
export class JobServiceInterface {
   baseUrl: string;
   token: string;
   callerInfo: JobServiceCallerInfo;

   public constructor(callerInfo: JobServiceCallerInfo) {
      this.callerInfo = callerInfo;
      this.baseUrl = SettingsManager.getSystemSetting("jobs.jobServiceHost");
      this.token = SettingsManager.getSystemSetting("jobs.jobServiceAPIKey");
   }

   header(token?: string) {
      let result = {
         "access-token": token || SettingsManager.getSystemSetting("jobs.jobServiceAPIKey")
      };
      result["user-ref"] = this.callerInfo.userRef;
      result["user-name"] = this.callerInfo.userName;
      result["organization-id"] = this.callerInfo.organizationId;
      // result["language-code"] = this.callerInfo.languageCode; 
      return result;
   }

   /** Get Possible Jobtypes */
   async JobListing(): Promise<string[]>
   /** Get Info on specified JobType */
   async JobListing(jobType: string): Promise<JobInfoType[]>
   async JobListing(jobType: string = "") {
      return await fetch(`${this.baseUrl}/job/info/${jobType}`, {
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobListing");
      });
   }

   //#region Job Status

   /** Get all job's status and jobId  */
   async JobStatus(lvl?: "user" | "admin" | "oganization"): Promise<CompactJobStatus[]>
   /** Get extended status on specified JobId */
   async JobStatus(jobId: string): Promise<JobStatus>
   async JobStatus(jobId: string = "") {
      if (["", "user", "admin", "oganization"].includes(jobId)) {
         let lvl = jobId || "user";
         return await fetch(`${this.baseUrl}/job/status/?access=${lvl}`, {
            headers: this.header()
         }).then((response) => {
            if (response.status === 200) {
               return response.json();
            }
            JobServiceError(response, "JobStatus");
         });
      }
      return await fetch(`${this.baseUrl}/job/status/${jobId}`, {
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobStatus");
      });
   }

   //#region Create/Start/Stop/Remove Job

   /** Create Job with specified Jobtype and recieve JobId */
   async CreateJob(jobType: string, instanceName: string): Promise<CreateJobResp> {
      return await fetch(`${this.baseUrl}/job/create/${jobType}${instanceName ? "?name=" + instanceName : ""}`, {
         method: "GET",
         headers: this.header(undefined)
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "CreateJob");
      });
   }

   /** Start job with specified JobId */
   async StartJob(jobId: string): Promise<JobStatus> {
      return await fetch(`${this.baseUrl}/job/start/${jobId}`, {
         method: "GET",
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "StartJob");
      });
   }

   /** Stop job with specified JobId */
   async StopJob(jobId: string): Promise<ServiceMessage> {
      return await fetch(`${this.baseUrl}/job/stop/${jobId}`, {
         method: "GET",
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "StopJob");
      });
   }


   /** Stop and Remove job and all files with specified jobId */
   async RemoveJob(jobId: string): Promise<ServiceMessage> {
      return await fetch(`${this.baseUrl}/job/remove/${jobId}`, {
         method: "GET",
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "RemoveJob");
      });
   }

   async ResetJob(jobId: string): Promise<ServiceMessage> {
      return await fetch(`${this.baseUrl}/job/reset/${jobId}`, {
         method: "GET",
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "RemoveJob");
      });
   }


   //#region Files

   /** Get available result on specified JobId */
   async JobResult(jobId: string): Promise<string[]>
   /** Get file of specified JobId */
   async JobResult(jobId: string, filename): Promise<File>
   async JobResult(jobId: string, filename: string = "") {
      let resp = await fetch(`${this.baseUrl}/job/results/${jobId}/${filename}`, {
         headers: this.header()
      })
      if (filename != "")
         return await resp.blob();
      return await resp.json();
   }

   /** Upload files to specified JobId */
   async JobUpload(jobId: string, file: File[] | File | FileList) {
      let formData = new FormData();
      if (Array.isArray(file) || file instanceof FileList) {
         for (let f of file) {
            formData.append("file", f);
         }
      } else {
         formData.append("file", file);
      }
      return await fetch(`${this.baseUrl}/job/upload/${jobId}`, {
         method: "POST",
         headers: this.header(),
         body: formData
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobUpload");
      });
   }

   async JobUploadJson(jobId: string, name: string, json: string) {
      return await fetch(`${this.baseUrl}/job/uploadJson/${jobId}/${name}`, {
         method: "POST",
         headers: {
            ...this.header(),
            "content-type": "application/json"
         },
         body: json
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobUpload");
      });
   }

   async JobUploadData(jobId: string, data: any) {
      let formData = new FormData();
      formData.append("json", JSON.stringify(data));
      return await fetch(`${this.baseUrl}/job/upload/${jobId}`, {
         method: "POST",
         headers: this.header(),
         body: formData
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobUpload");
      });
   }

   async DebugFile(jobId: string): Promise<string[]>
   async DebugFile(jobId: string, filename: string): Promise<Blob>
   async DebugFile(jobId: string, filename: string = "") {
      let resp = await fetch(`${this.baseUrl}/job/files/${jobId}/${filename}`, {
         headers: this.header()
      })
      if (filename != "")
         return await resp.blob();
      return await resp.json();
   }

   //#region Log

   /** String log of specified JobId */
   async JobLog(jobId: string): Promise<string | undefined> {
      return await fetch(`${this.baseUrl}/job/log/${jobId}`, {
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.text();
         }
         JobServiceError(response, "JobLog");
      });
   }

   //#region Task

   /** Check if job has a TaskList */
   async JobHasTaskList(jobId: string): Promise<{ jobId: string, hasTaskDb: boolean }> {
      return await fetch(`${this.baseUrl}/job/task/${jobId}?check=true`, {
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobTaskList");
      });
   }

   /** Get list of Task   LIMIT 240*/
   async JobTaskList(jobId: string, nextGroupIdx?: number, status?: string): Promise<TaskList[]> {
      let params = new URLSearchParams(handleObjectUrlParams({
         nextGroupIdx: nextGroupIdx,
         status: status
      })).toString();

      return await fetch(`${this.baseUrl}/job/task/${jobId}` + (params ? ("?" + params) : ""), {
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobTaskList");
      });
   }

   /** Get an Array of rows with result and debug info */
   async JobTaskInfo(jobId: string, taskId: string): Promise<string[]> {
      return await fetch(`${this.baseUrl}/job/task/${jobId}/${taskId}`, {
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobTaskInfo");
      });
   }

   // #region Name&Settings

   async JobSetName(jobId: string, name: string) {
      return await fetch(`${this.baseUrl}/job/rename/${jobId}?name=${name}`, {
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobSetName");
      });
   }

   /** Upload settings to specified JobId */
   async JobUploadSettings(jobId: string, data: string) {
      return await fetch(`${this.baseUrl}/job/settings/${jobId}`, {
         method: "POST",
         headers: {
            ...this.header(),
            "content-type": "application/json"
         },
         body: data
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobUploadSettings");
      });
   }

   async JobGetSettings(jobId: string) {
      return await fetch(`${this.baseUrl}/job/settings/${jobId}`, {
         headers: this.header()
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobGetSettings");
      });
   }

   // #region MailingList 
   async subscribe(jobId: string, mail: string, language?: "da" | "en") {
      return await fetch(`${this.baseUrl}/job/mailinglist/${jobId}/subscribe?mail=${mail}` + (language ? `&language=${language}` : ""), {
         headers: this.header(),
         method: "POST"
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobSetMailTo");
      });
   }

   async unsubscribe(jobId: string, mail: string) {
      return await fetch(`${this.baseUrl}/job/mailinglist/${jobId}/unsubscribe?mail=${mail}`, {
         headers: this.header(),
         method: "POST"
      }).then((response) => {
         if (response.status === 200) {
            return response.json();
         }
         JobServiceError(response, "JobSetMailTo");
      });
   }

   // #region Service Control

   /** Get Server status (Unprotected) */
   async serviceStatus(): Promise<ServiceStatus> {
      return await fetch(`${this.baseUrl}/`).then((response) => response.json());
   }

   /** Stops server (should auto restart) */
   async StopServer() {
      return await fetch(`${this.baseUrl}/shutdown/`, {
         headers: this.header("TestTokenADMIN")
      }).then((response) => {
         if (response.status === 200) {
            return response.text();
         }
         JobServiceError(response, "StopServer");
      });
   }

   getSettingsForJob(jobType: string): { settings: SingleSetting[], settingsGroups: GroupInfo } {
      switch (jobType) {
         case "RejsetidscrawlerUrl":
            return {
               settings: JobSettingsDefBoth.settings,
               settingsGroups: JobSettingsDefBoth.settingsGroups
            }
         case "ViamapRouter":
            return {
               settings: JobSettingsDefNoTransit.settings,
               settingsGroups: JobSettingsDefNoTransit.settingsGroups
            }
         default:
            return {
               settings: JobSettingsDefBoth.settings,
               settingsGroups: JobSettingsDefBoth.settingsGroups
            }
      }
   }

   validateSettingsForJob(jobType: string, settings: any): { success: boolean, errorMessages?: string[] } {
      let errors: string[] = [];
      switch (jobType) {
         case "RejsetidscrawlerUrl":
         case "WebRejsetidsCrawler":
            {
               // check that date is not in the past
               if (settings["tidsvindue.date"]) {
                  let currentDate = new Date().toISOString().split("T")[0].replaceAll("-", "")
                  let settingsDate = settings["tidsvindue.date"];
                  if (settingsDate < currentDate) {
                     errors.push("Beregningsdato kan ikke være i fortiden");
                  }
               }
            }
            break;

         default:

      }
      if (errors.length > 0) {
         return { success: false, errorMessages: errors };
      } else {
         return { success: true };
      }
   }

   getJobTypeInputFields(jobType: string): InputFieldSpec[] {
      switch (jobType) {
         case "ViamapRouter":
            return JobSettingsDefNoTransit.inputFieldSpecs;

         case "WebRejsetidsCrawler":
         case "RejsetidscrawlerUrl":
            return JobSettingsDefBoth.inputFieldSpecs;

         default:
            return [];
      }

   }

}

function JobServiceError(resp: Response, endPoint: string) {
   switch (resp.status) {
      case 400:
         throw new Error(endPoint + " Bad Request");
      case 401:
         throw new Error(endPoint + " Unauthorized");
      case 404:
         throw new Error(endPoint + " Not Found");
      case 503:
         throw new Error(endPoint + " Service Unavailable");
      case 500:
         throw new Error(endPoint + " Internal Server Error");
      case 502:
         throw new Error(endPoint + " Bad Gateway");
      default:
         throw new Error(endPoint + " Unknown Error");
   }
}

export type ServiceStatus = {
   "status": "ok",
   "message": "Server is running",
   "uptimeSec": number,
   "uptimeReadable": string,
   "environment": string,
   "version": string,
   "nodeVersion": string,
   "started": string
}

export type JobStatus = CompactJobStatus & {
   name: string,
   userRef: string,
   userName: string,
   organizationId: string,
   mailTo: string,

   created: string,
   started: string,
   finished: string,

   taskDone: number
   taskFailed: number
   taskTotal: number
   taskXXX: number
   taskInvalid: number
   estimatedEnd: string
   statusMsg: string
   nextRun: string

}

export type TaskList = {
   "groupIdx": number,
   "groupName": string,
   "status": "success" | "error",
   "Count(idx)": number
}

export type CompactJobStatus = {
   id: string,
   type: string,
   status: string,
}

export type ServiceMessage = {
   message: string
}

export type CreateJobResp = {
   "message": string,
   "jobId": string
}

export type JobInfoType = {
   "name": string,
   "version": string,
   "da": {
      "name": string
      "description": string
   },
   "en": {
      "name": string
      "description": string
   },
   "inputParams": unknown, //TODO: Define input params
}

function handleObjectUrlParams(object: { [key: string]: any }): Record<string, string> {
   let nextObject = {};
   for (let key in object) {
      if (!object[key]) {
         continue;
      } else if (typeof object[key] == "number") {
         nextObject[key] = object[key].toString();
      } else if (object[key] instanceof Array) {
         nextObject[key] = object[key].join(",");
      } else {
         nextObject[key] = object[key].toString();
      }
   }
   return nextObject;
}